import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  BackButton,
  Breadcrums,
  MUIButton,
  MUIGrid,
  MUIPagination,
  SimpleDropdown,
} from '../../Components/Shared'
import CONSTANTS, { BREADCRUMS, PAGE_NAME, PAGES_ROUTES } from '../../Constants'
import { BreadcrumsModel } from '../../Models'
import './AllNotifications.scss'
import { NOTIFICATIONS } from '../../Constants/ApiDefinations'
import mainApiService from '../../Services'
import { Typography } from '@mui/material'
import ListingFilter from '../../Components/Atoms/ListingFilter'
import { getNotificationFilterFields } from '../../Constants/Constant'
import { NotificationTabType } from '../../Models/CommonListHeadingModal'
import { logger } from '../../Helpers'

const AllNotification = () => {
  const navigate = useNavigate()
  const [tab, setTab] = useState<NotificationTabType>('my')
  const [notifications, setNotifications] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [filters, setFilters] = useState<any[]>([])
  const [clearFilterTrigger, setClearFilterTrigger] = useState(0)
  const [eventOptions, setEventOptions] = useState<{ key: string; value: string }[]>([])
  const moduleRouteMap: Record<string, string> = {
    widget: PAGES_ROUTES.ManageWidget,
    container: PAGES_ROUTES.ManageContainer,
    sitenavigation: PAGES_ROUTES.SiteNavigation,
    bulkoperation: PAGES_ROUTES.BulkOperation,
  }
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()

  const activeNotificationTab = searchParams.get('tab')

  const filterFields = getNotificationFilterFields(eventOptions)

  const buildApiFilters = (filters: any[]) => {
    return filters
      .filter((filter) => filter.filterBy === 'event' || filter.filterBy === 'readStatus')
      .map((filter) => {
        if (filter.filterBy === 'event') {
          return {
            filterBy: 'eventCode',
            filterCondition: 'in',
            filterValue: filter.filterValue,
          }
        }
        if (filter.filterBy === 'readStatus') {
          return {
            filterBy: 'isRead',
            filterCondition: 'equals',
            filterValue: filter.filterValue[0] === 'read',
          }
        }
        return null
      })
      .filter(Boolean)
  }

  const fetchNotifications = async (
    myNotification: boolean,
    pageNum = page,
    pageLimit = limit,
    appliedFilters = filters,
  ) => {
    setLoading(true)
    try {
      const apiFilters = buildApiFilters(appliedFilters)
      const payload: {
        myNotification: boolean
        filters: ({ filterBy: string; filterCondition: string; filterValue: any } | null)[]
        sortType: number
        page: number
        limit: number
        date?: string | null
      } = {
        myNotification,
        filters: apiFilters,
        sortType: -1,
        page: pageNum,
        limit: pageLimit,
      }
      if (selectedDate) {
        payload.date = selectedDate
      }
      const notificationList = NOTIFICATIONS.NOTIFICATION_LIST(payload)
      const response: any = await mainApiService(notificationList)
      setNotifications(
        Array.isArray(response.data?.notifications) ? response.data.notifications : [],
      )
      setTotalCount(response?.data?.count || 0)
    } catch (error) {
      setNotifications([])
      setTotalCount(0)
      logger.error(error)
    }
    setLoading(false)
  }

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const eventsResponse: any = await mainApiService(NOTIFICATIONS.NOTIFICATION_EVENTS())
        const options = (eventsResponse?.data || []).map((e: any) => ({
          key: e.eventCode,
          value: e.label,
        }))
        setEventOptions(options)
      } catch {
        setEventOptions([])
      }
    }
    fetchEvents()
  }, [])
  useEffect(() => {
    if (!activeNotificationTab) return
    if (activeNotificationTab === 'my' || activeNotificationTab === 'others') {
      setTab(activeNotificationTab)
    } else {
      setSearchParams({ tab: 'my' }, { replace: true })
    }
  }, [activeNotificationTab])
  useEffect(() => {
    setPage(1)
  }, [tab])
  useEffect(() => {
    fetchNotifications(tab === 'my', page, limit, filters)
  }, [tab, page, limit, filters, selectedDate])

  const handleMarkAllAsRead = async () => {
    try {
      const payload = { markAllAsRead: true }
      await mainApiService(NOTIFICATIONS.NOTIFICATION_MARK_AS_READ(payload))
      fetchNotifications(tab === 'my', page, limit, filters)
    } catch (error) {
      logger.error(error)
    }
  }

  const handleNotificationClick = async (item: any) => {
    try {
      const notificationId = item?.userNotificationStatus?.notificationId
      if (notificationId) {
        const payload = { markAllAsRead: false, notificationId }
        await mainApiService(NOTIFICATIONS.NOTIFICATION_MARK_AS_READ(payload))
      }
      const module = (item?.module || '').toLowerCase()
      const baseRoute = moduleRouteMap[module]
      if (item?.eventCode === 'bulk.state.change') {
        navigate(`/${baseRoute}/${PAGES_ROUTES.BulkStateListing}`)
        return
      }
      navigate(`/${baseRoute}/${PAGES_ROUTES.PageTypeView}/${item?.payload?.contentDocId}`)
    } catch (error) {
      logger.error(error)
    }
  }

  const handlePerPageView = (e: any) => {
    setLimit(Number(e.target.value))
    setPage(1)
  }

  const handlePaginationChange = (_event: any, value: number) => {
    setPage(value)
  }

  const handleFilterSubmit = (filterObj: any, id?: string) => {
    if (!filterObj || Object.keys(filterObj).length === 0) {
      setFilters((prev: any[]) => prev.filter((filter) => filter.filterBy !== id))
      if (id === 'date') setSelectedDate(null)
      setPage(1)
      return
    }
    if (filterObj.filterBy === 'date') {
      const dateObj = filterObj.filterValue
      let dateStr = null
      if (dateObj) {
        const year = dateObj.getFullYear()
        const month = String(dateObj.getMonth() + 1).padStart(2, '0')
        const day = String(dateObj.getDate()).padStart(2, '0')
        dateStr = `${year}-${month}-${day}`
      }
      setSelectedDate(dateStr)
      setPage(1)
      return
    }
    setFilters((prev: any[]) => {
      const filtered = prev.filter((filter) => filter.filterBy !== filterObj.filterBy)
      if (!filterObj.filterValue || filterObj.filterValue.length === 0) {
        return filtered
      }
      return [...filtered, filterObj]
    })
    setPage(1)
  }
  const handleTabClick = (tabTitle: NotificationTabType) => {
    setTab(tabTitle)
    if (activeNotificationTab) {
      searchParams.delete('tab')
      setSearchParams(searchParams)
    }
  }
  const hasUnread = notifications.some(
    (item) => item.userNotificationStatus && !item.userNotificationStatus.isRead,
  )
  const displayNotificationItemList = () => {
    if (loading) return <div style={{ padding: 24, textAlign: 'center' }}>Loading...</div>
    if (notifications.length === 0)
      return (
        <div className='notification-empty'>
          <div className='notification-list-empty-title'>No results found.</div>
          <div className='notification-list-empty-desc'>
            You can modify the filters or reset them to view all results.
          </div>
        </div>
      )

    return notifications.map((item, idx) => (
      <button
        key={item._id + idx}
        className={`notification-item${item.userNotificationStatus?.isRead ? '' : ' unread'}`}
        type='button'
        onClick={() => handleNotificationClick(item)}
        data-testid={`${item.title.toLowerCase()} ${item._id}`}
      >
        <div
          className='notification-title-row'
          style={{ display: 'flex', alignItems: 'center', position: 'relative' }}
        >
          <div className='notification-list-item-title'>{item.title}</div>
          {!item.userNotificationStatus?.isRead && <span className='notification-list-dot' />}
        </div>
        <div className='notification-list-item-desc'>{item.description}</div>
        <div className='notification-list-item-date'>
          {item.updatedAt
            ? `${new Date(item.updatedAt).toLocaleDateString()} ${new Date(
                item.updatedAt,
              ).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
              })}`
            : ''}
        </div>
      </button>
    ))
  }

  return (
    <div className='listing-screen p-16'>
      <form>
        <div style={{ paddingTop: '8px', paddingBottom: '8px' }}>
          <Breadcrums breadcrums={BREADCRUMS.ALL_NOTIFICATION() as BreadcrumsModel[]} />
        </div>
        <div
          style={{ display: 'flex', alignItems: 'center', paddingTop: '8px', paddingBottom: '8px' }}
        >
          <BackButton
            label={PAGE_NAME.AllNotification}
            className='col73'
            handleOnClick={() => navigate(-1)}
          />
          <button
            className='notification-button border-none transparent-background'
            onClick={hasUnread ? handleMarkAllAsRead : undefined}
            style={{
              cursor: hasUnread ? 'pointer' : 'default',
              opacity: hasUnread ? 1 : 0.5,
              pointerEvents: hasUnread ? 'auto' : 'none',
              color: '#402080',
            }}
          >
            Mark All as Read
          </button>
        </div>
        <div className='notification-header-row'>
          <div className='notification-tab'>
            <div style={{ display: 'flex' }}>
              <button
                className={`notification-tab${
                  tab === 'my' ? ' selected' : ''
                } border-none transparent-background`}
                type='button'
                onClick={() => handleTabClick('my')}
              >
                <span className='notif-tab-text'> My Notifications </span>
              </button>
              <button
                className={`notification-tab${
                  tab === 'others' ? ' selected' : ''
                } border-none transparent-background`}
                type='button'
                onClick={() => handleTabClick('others')}
              >
                <span className='notif-tab-text'>Others</span>
              </button>
            </div>
          </div>
          <div className='notification-filter'>
            {(filters.length > 0 || selectedDate) && (
              <MUIButton
                label='Clear All'
                variant='text'
                fontSize='16px'
                width={100}
                className='clear-filters-btn'
                type='button'
                handleClickCB={() => {
                  setFilters([])
                  setSelectedDate(null)
                  setClearFilterTrigger((prev) => prev + 1)
                }}
              />
            )}
            {filterFields.map((item) => (
              <ListingFilter
                key={item.id}
                filters={filters.find((it: any) => it.filterBy === item.id)?.filterValue || []}
                searchable={item.searchable}
                lable={item.lable}
                id={item.id}
                options={item.options ?? []}
                searchFunction={handleFilterSubmit}
                isFilterSingleSelect={item.isFilterSingleSelect}
                clearFilter={true}
                clearFilterTrigger={clearFilterTrigger}
                type={item.type === 'date' ? 'date' : undefined}
              />
            ))}
          </div>
        </div>
        <div className='notification-content-wrapper'>
          <div className='notification-table'>{displayNotificationItemList()}</div>
          <MUIGrid
            container
            className='d-flex align-items-center justify-content-between c-pagination notification-pagination'
          >
            {notifications.length > 0 && (
              <MUIGrid item direction='row' className='d-flex align-items-center col1'>
                <SimpleDropdown
                  selectId='pageId'
                  options={CONSTANTS.PAGINATION_OPTIONS}
                  handleChange={handlePerPageView}
                  value={limit}
                />
                <Typography paddingX={3}>
                  Showing {notifications.length} of {totalCount} records
                </Typography>
              </MUIGrid>
            )}
            <MUIGrid item>
              {notifications.length > 0 && totalCount > 0 && (
                <MUIPagination
                  testId='userPagination'
                  onChange={handlePaginationChange}
                  count={Math.ceil(totalCount / limit)}
                  page={page}
                />
              )}
            </MUIGrid>
          </MUIGrid>
        </div>
      </form>
    </div>
  )
}

export default AllNotification
